import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Clock,
  CheckCircle2,
  StickyNote,
  User as UserIcon,
  ExternalLink,
  Activity,
  Loader2,
  Trash2,
  RotateCcw,
  Ban,
} from "lucide-react";

import AppTopbar from "@/components/AppTopbar";
import BottomNav from "@/components/BottomNav";
import Loader from "@/components/Loader";
import SupervisorsBlock from "@/components/SupervisorsBlock";
import DebriefBlock from "@/components/DebriefBlock";
import TakeChargeBlock from "@/components/TakeChargeBlock";
import PoliceCalledBlock from "@/components/PoliceCalledBlock";
import Countdown from "@/components/Countdown";
import ConfirmDialog from "@/components/ConfirmDialog";
import { getAlert, closeAlert } from "@/lib/alerts";
import { adminDeleteAlert, adminRestoreAlert } from "@/lib/admin";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LEVEL_META, formatDateTimeFr, timeAgoFr } from "@/lib/format";

export default function AlertDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const a = await getAlert(id);
      setAlert(a);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loader />;

  if (error || !alert) {
    return (
      <div className="min-h-screen bg-[var(--sa-subtle)] pb-24">
        <AppTopbar />
        <main className="mx-auto max-w-xl px-4 pt-5">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error || "Alerte introuvable."}
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  const meta = LEVEL_META[alert.level] || LEVEL_META.green;
  const isOwner = alert.agent_id === user?.id;
  const isAdmin = user?.role === "admin";
  const isTaker = alert.level === "red" && alert.taken_by === user?.id;
  const canClose =
    alert.status === "active" &&
    (alert.level === "green" ? isOwner || isAdmin : alert.level === "red" ? isOwner || isTaker || isAdmin : false);

  const mapsUrl = alert.location
    ? `https://www.google.com/maps/search/?api=1&query=${alert.location.lat},${alert.location.lng}`
    : alert.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(alert.address)}`
    : null;

  const handleClose = async () => {
    const isRed = alert.level === "red";
    setClosing(true);
    try {
      const updated = await closeAlert(alert.id, {
        reason: isRed ? "resolved" : "manual",
      });
      setAlert(updated);
      toast.success(isRed ? "Urgence clôturée." : "Alerte clôturée.");
      setCloseOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--sa-subtle)] pb-24" data-testid="alert-detail-screen">
      <AppTopbar />
      <main className="mx-auto max-w-xl px-4 pt-5">
        <button
          onClick={() => navigate(-1)}
          data-testid="detail-back"
          className="mb-4 inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Retour
        </button>

        {alert.is_deleted ? (
          <div
            data-testid="alert-deleted-banner"
            className="mb-4 flex items-start justify-between gap-3 rounded-[16px] border border-slate-300 bg-slate-100 px-4 py-3"
          >
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                <Ban size={14} />
              </span>
              <div>
                <p className="text-[13px] font-bold uppercase tracking-wider text-slate-700">
                  Alerte supprimée
                </p>
                <p className="text-[12px] text-slate-600">
                  par {alert.deleted_by_name || "—"}
                  {alert.deleted_at
                    ? ` ${timeAgoFr(alert.deleted_at)} (${formatDateTimeFr(alert.deleted_at)})`
                    : ""}
                </p>
              </div>
            </div>
            {isAdmin ? (
              <button
                type="button"
                onClick={async () => {
                  setAdminBusy(true);
                  try {
                    const r = await adminRestoreAlert(alert.id);
                    setAlert(r);
                    toast.success("Alerte restaurée.");
                  } catch (err) {
                    toast.error(formatApiError(err));
                  } finally {
                    setAdminBusy(false);
                  }
                }}
                disabled={adminBusy}
                data-testid="alert-admin-restore"
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                <RotateCcw size={12} /> Restaurer
              </button>
            ) : null}
          </div>
        ) : null}

        {alert.level === "red" && alert.status === "active" && !alert.is_deleted ? (
          <div
            className="mb-4 sa-red-pulse rounded-[16px] px-5 py-3 text-white"
            style={{ background: "#EF4444" }}
            data-testid="red-pulse-banner"
          >
            <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest">
              <span className="inline-block h-2 w-2 rounded-full bg-white sa-dot-pulse" />
              URGENCE EN COURS
            </div>
            <div className="mt-1 text-[15px] font-bold">
              Déclenchée {timeAgoFr(alert.created_at)} —{" "}
              {alert.agent_snapshot?.full_name}
            </div>
          </div>
        ) : null}

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="sa-card overflow-hidden"
        >
          {/* Colored header strip */}
          <div
            style={{
              background: `linear-gradient(135deg, ${meta.color}, ${meta.ink})`,
            }}
            className="px-5 py-4 text-white"
            data-testid="detail-header"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-5 items-center rounded-full bg-white/25 px-2 text-[10px] font-bold uppercase tracking-wider">
                  {meta.label}
                </span>
                <span className="inline-flex h-5 items-center rounded-full bg-white/15 px-2 text-[10px] font-semibold uppercase tracking-wider">
                  {alert.status === "active" ? "Active" : "Clôturée"}
                </span>
              </div>
              <span className="font-mono text-[11px] text-white/80">
                #{alert.short_id}
              </span>
            </div>
            <h1
              className="mt-3 text-[22px] font-bold"
              style={{ letterSpacing: "-0.02em" }}
            >
              {alert.agent_snapshot?.full_name || "—"}
            </h1>
            <p className="mt-0.5 text-[12.5px] text-white/85">
              Déclenché {timeAgoFr(alert.created_at)} · {formatDateTimeFr(alert.created_at)}
            </p>
            {alert.level === "orange" && alert.status === "active" && alert.reminder_at ? (
              <p
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold"
                data-testid="orange-reminder-strip"
              >
                <Countdown targetIso={alert.reminder_at} prefix="Rappel dans" />
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-4 px-5 py-5">
            {/* Agent */}
            <Row icon={UserIcon} label="Agent">
              <div className="flex flex-col">
                <span className="text-[14px] font-semibold text-slate-900">
                  {alert.agent_snapshot?.full_name}
                </span>
                <span className="text-[12px] text-slate-500">
                  {alert.agent_snapshot?.email}
                </span>
              </div>
              {alert.agent_snapshot?.phone ? (
                <a
                  href={`tel:${alert.agent_snapshot.phone}`}
                  data-testid="detail-phone"
                  className="flex items-center gap-1.5 rounded-full bg-[var(--sa-blue-soft)] px-3 py-1.5 text-[12px] font-semibold text-[var(--sa-blue-dark)] transition hover:bg-[#dbeafe]"
                >
                  <Phone size={12} /> {alert.agent_snapshot.phone}
                </a>
              ) : null}
            </Row>

            {/* Address */}
            <Row icon={MapPin} label="Adresse">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-[14px] font-medium text-slate-900 leading-snug">
                  {alert.address || "Non renseignée"}
                </span>
                {alert.location ? (
                  <span className="mt-0.5 text-[11px] font-mono text-slate-400">
                    {alert.location.lat.toFixed(5)},{" "}
                    {alert.location.lng.toFixed(5)} ·{" "}
                    {alert.address_source === "gps" ? "source GPS" : "source manuelle"}
                  </span>
                ) : (
                  <span className="mt-0.5 text-[11px] text-slate-400">
                    source manuelle
                  </span>
                )}
              </div>
              {mapsUrl ? (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="detail-maps"
                  className="flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <ExternalLink size={12} />
                  Google Maps
                </a>
              ) : null}
            </Row>

            {alert.client_name ? (
              <Row icon={UserIcon} label="Client">
                <span className="text-[14px] font-medium text-slate-900">
                  {alert.client_name}
                </span>
              </Row>
            ) : null}

            {alert.notes ? (
              <Row icon={StickyNote} label="Notes">
                <span className="text-[13.5px] text-slate-700 leading-snug whitespace-pre-line">
                  {alert.notes}
                </span>
              </Row>
            ) : null}

            {/* Close status */}
            {alert.status === "closed" ? (
              <div
                className="rounded-[12px] border border-slate-200 bg-[var(--sa-subtle)] px-4 py-3"
                data-testid="detail-closed-badge"
              >
                <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-600">
                  <CheckCircle2 size={14} className="text-[var(--sa-blue)]" />
                  Clôturée {timeAgoFr(alert.closed_at)}
                </div>
                <p className="mt-1 text-[12.5px] text-slate-500">
                  {formatDateTimeFr(alert.closed_at)} · motif : {alert.closed_reason || "—"}
                </p>
              </div>
            ) : null}

            {canClose ? (
              <button
                type="button"
                onClick={() => setCloseOpen(true)}
                disabled={closing}
                data-testid="detail-close-button"
                className="sa-btn-primary mt-1"
              >
                {closing ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Clôture…
                  </span>
                ) : alert.level === "red" ? (
                  "Clôturer l'urgence"
                ) : (
                  "Clôturer le RDV"
                )}
              </button>
            ) : null}

            {isAdmin && !alert.is_deleted ? (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                disabled={adminBusy}
                data-testid="alert-admin-delete"
                className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-[14px] border border-red-200 bg-white px-4 py-2.5 text-[13px] font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-60"
              >
                <Trash2 size={14} />
                Supprimer cette alerte (admin)
              </button>
            ) : null}
          </div>
        </motion.section>

        {/* RED-specific: tel:17 + take-charge + police-called */}
        {alert.level === "red" && alert.status === "active" ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 flex flex-col gap-3"
            data-testid="red-actions"
          >
            <PoliceCalledBlock alert={alert} onUpdated={setAlert} />
            <TakeChargeBlock alert={alert} user={user} onUpdated={setAlert} />
          </motion.div>
        ) : null}

        {/* Orange-specific: supervision + debrief */}
        {alert.level === "orange" ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.04, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 flex flex-col gap-4"
          >
            {alert.status === "active" ? (
              <SupervisorsBlock alert={alert} user={user} onUpdated={setAlert} />
            ) : null}
            <DebriefBlock alert={alert} user={user} onUpdated={setAlert} />
          </motion.div>
        ) : null}

        {/* Timeline */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="sa-card mt-4 px-5 py-5"
          data-testid="detail-timeline"
        >
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-slate-500" />
            <h2 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500">
              Journal
            </h2>
          </div>
          <ol className="mt-3 flex flex-col">
            {alert.timeline.map((ev, idx) => (
              <li key={idx} className="relative flex gap-3 pb-4 last:pb-0">
                {idx < alert.timeline.length - 1 ? (
                  <span
                    aria-hidden
                    className="absolute left-[9px] top-5 h-full w-[2px] bg-slate-200"
                  />
                ) : null}
                <span
                  className="relative z-10 mt-1 h-[18px] w-[18px] shrink-0 rounded-full border-[3px] border-white"
                  style={{
                    background:
                      ev.type === "closed" ? "#64748B" : "var(--sa-blue)",
                    boxShadow: "0 0 0 2px #CBD5E1",
                  }}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-[13px] font-semibold text-slate-900">
                    {labelForEvent(ev.type)}
                  </span>
                  <span className="text-[11.5px] text-slate-500">
                    par {ev.by_name || "—"} ·{" "}
                    <Clock
                      size={10}
                      className="inline -translate-y-[1px] text-slate-400"
                    />{" "}
                    {formatDateTimeFr(ev.at)}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </motion.section>

        <p className="mt-8 pb-2 text-center text-[11px] font-medium text-slate-400">
          Propulsé par C l'Agence
        </p>
      </main>
      <BottomNav />

      <ConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={alert.level === "red" ? "Clôturer l'urgence ?" : "Clôturer le rendez-vous ?"}
        description={
          alert.level === "red"
            ? "Confirmer que la situation est résolue. La clôture met fin à l'alerte pour toute l'équipe."
            : "Cette action est définitive. L'alerte n'apparaîtra plus parmi les actives."
        }
        confirmLabel={alert.level === "red" ? "Clôturer l'urgence" : "Clôturer"}
        tone={alert.level === "red" ? "danger" : "primary"}
        loading={closing}
        onConfirm={handleClose}
        testId="alert-close-confirm"
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Supprimer cette alerte ?"
        description={`Confirmer la suppression. Cette alerte n'apparaîtra plus dans l'historique. Vous pourrez la restaurer ensuite via « Voir les supprimées ».`}
        confirmLabel="Supprimer"
        tone="danger"
        loading={adminBusy}
        onConfirm={async () => {
          setAdminBusy(true);
          try {
            await adminDeleteAlert(alert.id);
            // Optimistic update so the banner appears immediately,
            // independent of any refetch race.
            setAlert((prev) =>
              prev
                ? {
                    ...prev,
                    is_deleted: true,
                    deleted_at: new Date().toISOString(),
                    deleted_by: user?.id || null,
                    deleted_by_name: user?.full_name || "Admin",
                  }
                : prev
            );
            toast.success("Alerte supprimée.");
            setDeleteOpen(false);
            // Background refetch (admin auto-includes deleted) — picks up
            // the persisted timeline event and any server-side fields.
            try {
              const fresh = await getAlert(alert.id);
              if (fresh) setAlert(fresh);
            } catch (_e) {
              /* ignore — optimistic state already correct */
            }
          } catch (err) {
            toast.error(formatApiError(err));
          } finally {
            setAdminBusy(false);
          }
        }}
        testId="alert-admin-delete-confirm"
      />
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-slate-50 text-slate-500">
        <Icon size={16} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {label}
        </span>
        <div className="mt-0.5 flex min-w-0 flex-1 flex-wrap items-start justify-between gap-2">
          {children}
        </div>
      </div>
    </div>
  );
}

function labelForEvent(type) {
  switch (type) {
    case "created":
      return "Alerte créée";
    case "closed":
      return "Alerte clôturée";
    case "notified":
      return "Notification envoyée";
    case "supervise_assigned":
      return "Superviseur enregistré";
    case "supervise_released":
      return "Superviseur retiré";
    case "reminder_sent":
      return "Rappel envoyé";
    case "debriefed":
      return "Compte-rendu";
    case "escalated_from_orange":
      return "Escalade en feu rouge";
    case "take_charge":
      return "Prise en charge";
    case "police_called":
      return "Police / secours contactés";
    case "taken":
      return "Prise en charge";
    case "police_called":
      return "Police contactée";
    default:
      return type;
  }
}
